import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import type { LegalCaseListItem } from '../types/LegalCase';
import {
    LEGAL_CASE_FILTER_DEFS,
    emptyFilterValues,
    toQueryParams,
    type FilterValues,
} from '../components/pages/recovery/shared/filterTypes';

export default function useLegalCasePageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('-openedAt');
    // Estado dos filtros genérico e data-driven: um único record derivado das definições, em
    // vez de um useState por filtro. Adicionar um filtro é uma entrada em LEGAL_CASE_FILTER_DEFS
    // — o estado, o request e os chips saem daí, sem mexer aqui.
    const [filterValues, setFilterValues] = useState<FilterValues>(() =>
        emptyFilterValues(LEGAL_CASE_FILTER_DEFS),
    );
    const [rows, setRows] = useState<LegalCaseListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const size = 100;
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterCounts, setFilterCounts] = useState<Record<string, any>>({});
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    // Serializa os filtros ativos para a dependência do efeito (o record muda de referência a
    // cada set, então uma string estável evita refetch redundante).
    const filterParams = toQueryParams(LEGAL_CASE_FILTER_DEFS, filterValues);
    const filterKey = JSON.stringify(filterParams);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await recoveryApi.getLegalCases({
                page,
                size,
                sort: sort || undefined,
                search: search || undefined,
                ...toQueryParams(LEGAL_CASE_FILTER_DEFS, filterValues),
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setFilterCounts(result.filterCounts || {});
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
        // filterValues entra via filterKey (string estável); incluí-lo direto refetchava à toa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search, sort, filterKey, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    // Muda um filtro pelo id e volta para a primeira página (o conjunto de resultados mudou).
    const setFilterValue = (id: string, value: string | boolean) => {
        setPage(1);
        setFilterValues((prev) => ({ ...prev, [id]: value }));
    };

    const clearFilters = () => {
        setPage(1);
        setFilterValues(emptyFilterValues(LEGAL_CASE_FILTER_DEFS));
    };

    // Clique na linha navega para a página de detalhe dedicada (não abre mais painel
    // lateral): o detalhe tem navegação por seção e escala para as próximas tasks.
    const handleRowClick = (row: LegalCaseListItem) => {
        navigate(`/recovery/legal-cases/${row.id}`);
    };

    return {
        rows, total, page, setPage, size,
        isLoading, initialLoading, filterCounts,
        searchInput, setSearchInput, search, setSearch,
        sort, setSort,
        filterValues, setFilterValue, clearFilters,
        refresh, handleRowClick,
        apiInfo,
    };
}
